<template>
  <footer class="yolee-footer">
    <div class="footer-inner">
      <!-- 分类区 -->
      <div class="footer-category">
        <!-- desktop layout -->
        <div
          class="category-desktop"
          v-if="!isMobileView"
          role="navigation"
          aria-label="Product categories"
        >
          <div class="category-item" v-for="sub in listData" :key="sub.id">
            <div
              class="category-header"
              @click="navigateToSubCategory(sub.id)"
              @keydown.enter.prevent="navigateToSubCategory(sub.id)"
              role="button"
              tabindex="0"
            >
              <span class="category-item-title">{{ sub.name }}</span>
            </div>

            <ul
              class="product-list"
              v-if="Array.isArray(sub.products) && sub.products.length"
            >
              <li
                v-for="product in sortedProducts(sub.products)"
                :key="product.productId"
                @click="navigateToProduct(product.productId)"
                @keydown.enter.prevent="navigateToProduct(product.productId)"
                role="button"
                tabindex="0"
              >
                {{ product.name }}
              </li>
            </ul>
          </div>
        </div>

        <!-- mobile layout (accordion) -->
        <ul class="category-list-mobile" v-else>
          <li
            v-for="(sub, index) in listData"
            :key="sub.id"
            class="category-item"
          >
            <div
              class="category-header"
              @click="handleCategoryClick(index, sub.id)"
              @keydown.enter.prevent="handleCategoryClick(index, sub.id)"
              role="button"
              tabindex="0"
            >
              <span>{{ sub.name }}</span>
              <i class="arrow" aria-hidden="true">
                <Icon
                  :fontName="expandedIndex === index ? 'arrowUp' : 'arrowDown'"
                />
              </i>
            </div>

            <ul class="product-list" v-show="expandedIndex === index">
              <li
                v-for="product in sortedProducts(sub.products || [])"
                :key="product.productId"
                @click="navigateToProduct(product.productId)"
                @keydown.enter.prevent="navigateToProduct(product.productId)"
                role="button"
                tabindex="0"
              >
                {{ product.name }}
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <!-- 表单和SNS -->
      <div
        id="inquiry-form"
        class="footer-form"
        v-if="$route.path !== '/contact'"
      >
        <Form />
      </div>

      <!-- 地图与公司信息（优化后的 Get in Touch） -->
      <div class="footer-map" v-if="$route.path !== '/contact'">
        <ContactInfo ref="contactInfo" />
      </div>

      <!-- 底部链接 -->
      <div class="footer-bottom">
        <ul class="bottom-links">
          <li><a href="/policy">Privacy Policy</a></li>
          <li><a href="/terms-of-service">Terms of Service</a></li>
        </ul>
        <div class="copyright">
          © {{ new Date().getFullYear() }} YOLEE. All rights reserved.
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import Form from "@/components/Form";
import Icon from "@/components/Icon";
import ContactInfo from "@/components/ContactInfo";

export default {
  name: "Footer",
  components: {
    Form,
    Icon,
    ContactInfo,
  },
  props: {
    headData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      expandedIndex: null,
      isMobileView:
        typeof window !== "undefined" ? window.innerWidth <= 768 : true,
    };
  },
  computed: {
    // 收集所有子分类并按 id 升序排序（确保 id 为数字或可转为数字）
    listData() {
      const subArr = [];
      if (!Array.isArray(this.headData)) return [];
      this.headData.forEach((cat) => {
        if (!cat || !Array.isArray(cat.subCategories)) return;
        cat.subCategories.forEach((sub) => {
          // 防护：确保子对象至少包含 id 和 name
          if (!sub) return;
          subArr.push(sub);
        });
      });

      // 复制并按 numeric id 排序
      return subArr.slice().sort((a, b) => {
        const ai = Number(a.id);
        const bi = Number(b.id);
        if (Number.isNaN(ai) || Number.isNaN(bi)) {
          // 回退到字符串比较（保证稳定性）
          return String(a.id).localeCompare(String(b.id), undefined, {
            numeric: true,
          });
        }
        return ai - bi;
      });
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.handleScroll);
    this.handleScroll();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleResize() {
      this.isMobileView = window.innerWidth <= 768;
      if (!this.isMobileView) this.expandedIndex = null;
    },
    handleCategoryClick(index, subId) {
      if (this.isMobileView) {
        this.expandedIndex = this.expandedIndex === index ? null : index;
      } else {
        this.navigateToSubCategory(subId);
      }
    },
    navigateToSubCategory(subId) {
      // 防护：确保 subId 存在
      if (!subId && subId !== 0) return;
      this.$router.push(`/categories/sub/${subId}`);
    },
    navigateToProduct(productId) {
      if (!productId && productId !== 0) return;
      this.$router.push(`/product/${productId}`);
    },
    handleScroll() {
      const el = this.$refs.contactInfo && this.$refs.contactInfo.$el;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < windowHeight * 0.85) {
        el.classList.add("in-view");
      }
    },
    // helper: 在渲染处统一对产品排序（按 order 字段）
    sortedProducts(products = []) {
      if (!Array.isArray(products)) return [];
      return products.slice().sort((a, b) => {
        const ao = Number(a.order);
        const bo = Number(b.order);
        if (!Number.isNaN(ao) && !Number.isNaN(bo)) return ao - bo;
        return String(a.order).localeCompare(String(b.order), undefined, {
          numeric: true,
        });
      });
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.yolee-footer {
  color: @color-primary;
  padding: 1rem 2rem;
  font-size: 1rem;
  border-top: 1px solid @color-bg-light;

  .footer-inner {
    margin: auto;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .footer-category {
    .category-desktop {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .category-item {
      min-width: 220px;
      flex: 1 1 auto;
      border-bottom: 1px dashed fade(@color-text-sub, 20%);
      padding-bottom: 10px;

      .category-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: bold;
        cursor: pointer;
        transition: color 0.2s ease;

        &:hover {
          color: lighten(@color-primary, 20%);
        }

        .category-item-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .product-list {
        margin-top: 8px;
        li {
          cursor: pointer;
          position: relative;
          width: fit-content;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 3px 0;
          color: @color-text-sub;

          &::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 1px;
            background-color: @color-primary;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.3s ease;
          }

          &:hover {
            color: @color-primary;
          }

          &:hover::after {
            transform: scaleX(1);
          }
        }
      }
    }
  }

  .footer-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .footer-map {
    .contact-info {
      opacity: 0;
      transform: translateY(30px);
      transition: opacity 1s ease, transform 1s ease;

      &.in-view {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }

  .footer-bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .bottom-links {
      display: flex;
      gap: 20px;

      a {
        color: #ccc;
        text-decoration: none;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .yolee-footer {
    padding: 2rem;
  }
  .category-desktop {
    display: none;
  }
  .category-list-mobile {
    list-style: none;
    padding: 0;
    margin: 0;

    .category-item {
      width: 100%;
      border-bottom: 1px dashed fade(@color-text-sub, 20%);
      padding-bottom: 10px;
      margin-bottom: 10px;

      .category-header {
        font-size: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
      }

      .product-list {
        display: block;
        margin-top: 10px;
        padding-left: 12px;

        li {
          padding: 6px 0;
        }
      }
    }
  }
}

@media screen and (min-width: 769px) {
  .category-list-mobile {
    display: none;
  }
}
</style>
